import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Check,
  X,
  Edit3,
  Facebook,
  Instagram,
  Linkedin,
  Clock,
  AlertCircle,
  Inbox
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const NetworkIcon = ({ network, className }) => {
  const icons = {
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin
  };
  const Icon = icons[network];
  return Icon ? <Icon className={className} /> : null;
};

const NetworkBadge = ({ network }) => {
  const colors = {
    facebook: "network-facebook",
    instagram: "network-instagram",
    linkedin: "network-linkedin"
  };
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[network]}`}>
      <NetworkIcon network={network} className="w-3 h-3" />
      {network.charAt(0).toUpperCase() + network.slice(1)}
    </span>
  );
};

export default function ValidationQueue() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [processing, setProcessing] = useState({});
  const [scheduleDates, setScheduleDates] = useState({});

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    try {
      const response = await axios.get(`${API}/validation/queue`);
      setPosts(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement de la file de validation");
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async (postId) => {
    const local = scheduleDates[postId];
    if (!local) {
      toast.error("Choisissez d'abord une date et une heure");
      return;
    }
    const iso = new Date(local).toISOString();
    setProcessing(prev => ({ ...prev, [postId]: true }));
    try {
      await axios.post(`${API}/posts/${postId}/schedule`, null, { params: { scheduled_date: iso } });
      toast.success("Post planifié !");
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la planification");
    } finally {
      setProcessing(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleAction = async (postId, action, modifiedContent = null) => {
    setProcessing(prev => ({ ...prev, [postId]: true }));
    
    try {
      const payload = {
        post_id: postId,
        action: action
      };
      
      if (action === "modify" && modifiedContent) {
        payload.modified_content = modifiedContent;
      }
      
      await axios.post(`${API}/validation/action`, payload);
      
      const messages = {
        approve: "Post approuvé avec succès",
        modify: "Post modifié et approuvé",
        cancel: "Post rejeté"
      };
      
      toast.success(messages[action]);
      setEditingPost(null);
      fetchQueue();
    } catch (error) {
      toast.error("Erreur lors du traitement");
    } finally {
      setProcessing(prev => ({ ...prev, [postId]: false }));
    }
  };

  const openEditDialog = (post) => {
    setEditingPost(post);
    setEditedContent(post.content);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="ai-thinking">
          <span>Chargement de la file de validation...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up" data-testid="validation-queue">
      {/* Stats */}
      <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/20 border border-border">
        <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
          <Clock className="w-6 h-6 text-yellow-400" />
        </div>
        <div>
          <p className="text-2xl font-bold">{posts.length}</p>
          <p className="text-sm text-muted-foreground">Posts en attente de validation</p>
        </div>
      </div>

      {/* Queue */}
      {posts.length === 0 ? (
        <Card>
          <CardContent>
            <div className="empty-state py-16">
              <Inbox className="empty-state-icon w-16 h-16" />
              <h3 className="text-lg font-semibold mt-4">File de validation vide</h3>
              <p className="text-muted-foreground mt-2">
                Aucun post en attente de validation.<br />
                Les nouveaux contenus générés apparaîtront ici.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {posts.map((post) => (
            <Card 
              key={post.id} 
              className="post-preview"
              data-testid={`validation-post-${post.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <NetworkBadge network={post.network} />
                    {post.theme && (
                      <span className="text-sm text-muted-foreground">
                        Thème: {post.theme}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(post.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                {/* Content Preview */}
                <div className="bg-muted/30 rounded-lg p-4 mb-4">
                  <p className="whitespace-pre-wrap text-sm">{post.content}</p>
                </div>

                {/* Image Preview if exists */}
                {(post.image_base64 || post.image_url) && (
                  <div className="mb-4 rounded-lg overflow-hidden border border-border">
                    <img
                      src={post.image_base64 ? `data:image/png;base64,${post.image_base64}` : post.image_url}
                      alt="Post image"
                      className="w-full max-h-48 object-cover"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleAction(post.id, "approve")}
                    disabled={processing[post.id]}
                    data-testid={`approve-${post.id}`}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approuver
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => openEditDialog(post)}
                    disabled={processing[post.id]}
                    data-testid={`edit-${post.id}`}
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                  <input
                    type="datetime-local"
                    className="border border-border rounded-md px-2 py-1.5 text-sm bg-background"
                    value={scheduleDates[post.id] || ""}
                    onChange={(e) => setScheduleDates(prev => ({ ...prev, [post.id]: e.target.value }))}
                    data-testid={`schedule-date-${post.id}`}
                  />
                  <Button
                    variant="outline"
                    onClick={() => handleSchedule(post.id)}
                    disabled={processing[post.id]}
                    data-testid={`schedule-${post.id}`}
                  >
                    📅 Planifier
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                    onClick={() => handleAction(post.id, "cancel")}
                    disabled={processing[post.id]}
                    data-testid={`reject-${post.id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingPost} onOpenChange={() => setEditingPost(null)}>
        <DialogContent className="max-w-2xl" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-primary" />
              Modifier le contenu
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {editingPost && (
              <div className="mb-4">
                <NetworkBadge network={editingPost.network} />
              </div>
            )}
            <Textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              rows={10}
              className="w-full"
              data-testid="edit-content-textarea"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {editedContent.length} caractères
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPost(null)}>
              Annuler
            </Button>
            <Button 
              className="btn-primary"
              onClick={() => handleAction(editingPost?.id, "modify", editedContent)}
              data-testid="save-edit-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Enregistrer et approuver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
